import { NUTRIENT_KEYS } from '../../../lib/constants/constants';

const { ADDED_SUGARS, SATURATED_FAT, CHOLESTEROL, SODIUM, DIETARY_FIBER } = NUTRIENT_KEYS.nutrients;

/**
 * Determines if a nutrient is a limit nutrient
 *
 * @param {string} item
 * @returns {boolean} True if the nutrient is a limit nutrient, false otherwise
 */
export const getIsLimitNutrient = (item) => {
  return item && item !== DIETARY_FIBER ? [ADDED_SUGARS, SATURATED_FAT, CHOLESTEROL, SODIUM].includes(item) : false;
};

/**
 * Calculates scores for nutrient quality indicators
 * @param {Object} item - The nutrient data object containing current and DV values
 * @returns {Object} An object containing currentScore, goalScore, and their derived indicators
 */
export const calculateNutrientScores = (item) => {
  if (!item || !item.current || !item.dv) {
    return {
      currentScore: 0,
      goalScore: 0,
    };
  }

  // Calculate score based on how close current is to DV
  const current = Number(item.current);
  const dv = Array.isArray(item.dv) ? item.dv : [item.dv, item.dv];
  const minDv = dv[0];
  const maxDv = dv[1];

  // For nutrients that should be limited (like added sugars, saturated fat)
  // Explicitly exclude dietary fiber from limited nutrients
  const isLimitNutrient = getIsLimitNutrient(item.key);

  let currentScore;
  if (isLimitNutrient) {
    // For limit nutrients with both min and max:
    // - If current is below min, score is 10
    // - If current is above max, score is 1
    // - If current is between min and max, score is 8-9
    if (current <= minDv) {
      currentScore = 10;
    } else if (current >= maxDv) {
      currentScore = 1;
    } else {
      // Linear scale between min and max
      const ratio = (current - minDv) / (maxDv - minDv);
      currentScore = Math.max(8, Math.min(9, Math.round(9 - ratio)));
    }
  } else {
    // For other nutrients, higher is better
    // If current is 0, score is 1
    // If current is >= DV, score is 10
    // Linear scale in between
    const ratio = current / minDv;
    currentScore = Math.max(1, Math.min(10, Math.round(1 + ratio * 9)));
  }

  // Goal score is always 1 point higher than current, up to 10
  const goalScore = Math.min(10, currentScore + 1);

  return {
    currentScore,
    goalScore,
  };
};

/**
 * Calculates the change between current and DV values
 * @param {number} current - Current value
 * @param {number} dv - DV value
 * @param {string} unit - Unit of measurement
 * @returns {Object} An object containing the change value and its unit
 */
export const calculateNutrientChange = (current, dv, unit) => {
  // Check if inputs are valid numbers
  if (isNaN(current) || isNaN(dv)) {
    return { value: '', unit };
  }

  const value = dv - current;
  if (isNaN(value)) {
    return { value: '', unit };
  }

  const prefix = value > 0 ? '+' : '';
  return {
    value: value === 0 ? value : `${prefix}${value}`,
    unit,
  };
};

/**
 * Adjusts nutrient DV values based on daily calorie intake
 * @param {Object} nutrient - The nutrient data object
 * @param {number} dailyCalories - Daily calorie intake
 * @returns {Object} Adjusted nutrient data with updated DV values
 */
export const adjustNutrientDvForCalories = (nutrient, dailyCalories) => {
  if (!nutrient || !nutrient.dv || !dailyCalories) return nutrient;

  const { needsDvAdjustment } = getNutrientProperties(nutrient.key);

  if (needsDvAdjustment) {
    const dv = Array.isArray(nutrient.dv) ? nutrient.dv : [nutrient.dv, nutrient.dv];
    const minDv = dv[0] !== null ? Math.round(dv[0] * (dailyCalories / 2000)) : null;
    const maxDv = dv[1] !== null ? Math.round(dv[1] * (dailyCalories / 2000)) : null;

    return {
      ...nutrient,
      dv: [minDv, maxDv],
    };
  }

  return nutrient;
};

/**
 * Determines if a nutrient's DV should be adjusted based on calorie intake
 * @param {string} item - The key identifier for the nutrient
 * @returns {boolean} Whether this nutrient needs DV adjustment
 */
export const getNeedsDvAdjustment = (item) => {
  return item ? [ADDED_SUGARS, SATURATED_FAT, CHOLESTEROL, SODIUM].includes(item) : false;
};

/**
 * Gets comprehensive properties for a nutrient
 * @param {string} item - The key identifier for the nutrient
 * @returns {Object} Object containing nutrient properties
 */
export const getNutrientProperties = (item) => {
  return {
    isLimitNutrient: getIsLimitNutrient(item),
    needsDvAdjustment: getNeedsDvAdjustment(item),
  };
};
